import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {take} from 'lodash'
import {InputNumber, message, Image as AntdImage} from 'antd'
import ReactImageMagnify from 'react-image-magnify'
import {QuestionCircleOutlined, FilePdfOutlined} from '@ant-design/icons'
import './index.less'
import './mobile.less'
import Layout from '@/components/Layout'
import ProductItem from '@/components/ProductItem'
import OSwiper from 'src/components/OSwiper'
import noImage from '@/assets/img/icon/noImage.png'
import favoriteActiveIcon from '@/assets/img/icon/favoriteActive.png'
import favoriteDefaultIcon from '@/assets/img/icon/favoriteDefault.png'
import {
  addProductToFavoriteList,
  getMoreProducts,
  getProductDetail,
  getProductPdf,
  removeProductFromFavoriteList,
} from '@/api/req-api'
import {addProductToCart, removeCartItem, updateCartItemQuantity} from '@/store/modules/cart.slice'
import {
  checkLogin, getFormatMoney, getFormatProductListByCartList, getProductItemBtnType} from '@/common'
import SkuListView from './skulist'

export default function ProductDetail() {
  const {id} = useParams();
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const {t} = useTranslation()

  const commonState = useSelector(state => state.common)
  const cartState = useSelector(state => state.cart)
  const {productCategoryMap, isLogged, isInternal} = commonState
  const rootClassName = commonState.isMobile ? 'product-detail-mobile-container' : 'product-detail-container'
  const {orderCartProductList} = cartState

  const [nowImg, setNowImg] = useState('')
  const [widthData, setWidthData] = useState(window.innerWidth)
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 })
  const [productDetail, setProductDetail] = useState({})
  const [moreProducts, setMoreProducts] = useState([])
  const [quantity, setQuantity] = useState(1);
  const [productList, setProductList] = useState([])
  const [currentMedia, setCurrentMedia] = useState(null)
  const [productPdf, setProductPdf] = useState('')

  const useMoreProducts = useMemo(() => {
    return getFormatProductListByCartList(moreProducts, orderCartProductList)
  }, [moreProducts, orderCartProductList])

  const useProductList = useMemo(() => {
    return getFormatProductListByCartList(productList, orderCartProductList)
  }, [productList, orderCartProductList])

  // normalize images + videos into one array
  const mediaList = useMemo(() => {
    const imgs = productDetail?.Images || [];
    const vids = (productDetail?.videos || []).map(v => v.video_file);
    return [...imgs, ...vids]; // images first, then videos
  }, [productDetail])

  useEffect(() => {
    if (!nowImg) return

    const image = new Image();
    image.src = nowImg;
    image.onload = () => {
      setImgSize({ width: image.width, height: image.height });
    };
  }, [nowImg]);

  useEffect(() => {
    if (mediaList.length > 0) {
      setCurrentMedia(mediaList[0]);
    }
  }, [mediaList])

  // track whether current main media is image or video
  const isVideo = useMemo(() => {
    if (!currentMedia || typeof currentMedia !== 'string') return false;
    const m = currentMedia.toLowerCase();
    return m.includes('.mp4') || m.includes('.mov') || m.includes('/video/');
  }, [currentMedia])

  const fetchDetail = async (id) => {
    try {
      const {data: [productDetailResponse]} = await getProductDetail(id)
      setProductDetail(productDetailResponse)
      // setNowImg(productDetailResponse?.Images?.[0])
      setProductList(productDetailResponse.related_products || [])
      
      const productId = productDetailResponse?.Product_id;
      if (productId) {
        const pdfRes = await getProductPdf(productId);
        setProductPdf(pdfRes?.data?.record || [])
      }
      const {data: moreProductResponse} = await getMoreProducts(productDetailResponse.ProductGroup_id)
      setMoreProducts(take(moreProductResponse, 8))
    } catch (e) {
      //todo show error 
    }
  }
  const initPage = () => {
    if (id) {
      fetchDetail(id)
    }
  }
  const getPageWidth = ()=>{
    var getWidth = window.innerWidth;
    setWidthData(getWidth)
  }
  window.onresize = ()=>{
    getPageWidth()
  }
  useEffect(() => {
    initPage()
  }, [id])

  const renderProductImgSwiperSlideContent = (item, index) => {
    return <div
      className={'product-detail-top-info-left-img-container'}
      key={`product_detail_img_${index}`}
      onClick={(e)=>{setCurrentMedia(item)}}
    >
      <img
        src={item}
        className={item === currentMedia ? 'detail-swiper-img detail-swiper-img-special' : 'detail-swiper-img'}
      />
    </div>
  }

  const renderProductDetailImg = () => {
    const smallWidth = Math.min(imgSize.width, 400);
    const smallHeight = imgSize.height * (smallWidth / imgSize.width || 1);
    return (
      <div className={'product-detail-top-info-left-img-container-top'}>
        {!commonState.isMobile ? (
          <ReactImageMagnify {...{
            smallImage: {
              alt: '',
              isFluidWidth: true,
              src: currentMedia || noImage,
              width: smallWidth,
              height: smallHeight,
            },
            largeImage: {
              src: currentMedia || noImage,
              width: smallWidth * 2,
              height: smallHeight * 2,
            },
            enlargedImageContainerDimensions: {
              width: '150%',
              height: '150%',
            },
            enlargedImageContainerStyle:{
              zIndex: 9999
            }
          }} /> 
        ) : (
          <AntdImage src={currentMedia || noImage} />
        )}
    </div>
  )}

  const renderProductImgSwiper = () => <OSwiper
    className={'product-detail-top-info-left-product-img-swiper-container'}
    swiperClassName={'product-detail-top-info-left-product-img-swiper-swiper'}
    prevClassName={'product-detail-top-info-left-product-img-swiper-prev'}
    nextClassName={'product-detail-top-info-left-product-img-swiper-next'}
    data={mediaList}
    // data={productDetail?.Images && productDetail.Images.length > 0 ? productDetail.Images : [noImage]}
    renderSwiperSlideContent={renderProductImgSwiperSlideContent}
    // isShowPagination={true}
    // isShowNavigation={false}
    isShowPagination={false}
    isShowNavigation={true}
    slidesPerView={commonState.isMobile ? 2 : Math.floor(window.innerWidth/350)}
  />

  const renderSwiperSlideContent = (item, index) => {
    return <ProductItem
      img={item.Thumbnail}
      price={item.Price}
      name={item.Name}
      description={item.Description}
      isShowPrice={true}
      isFormatPriceToMoney={true}
      onBtnClick={() => handleBeforeAddToCart(item, 1)}
      onProductItemClick={() => handleProductClick(item)}
      isFavorited={item.IsFavorite}
      isShowFavorite={true}
      onFavoriteIconClick={() => handleFavoriteIconClick(item)}
      quantity={item.Quantity}
      btnType={getProductItemBtnType(item.Quantity)}
      onMinBtnClick={() => handleMinBtnClick(item, item.Quantity)}
      onPlusBtnClick={() => handleAddToCart(item)}
      isPromotion={item.IsPromotion}
      stock={item.InventoryQuantity}
      inventoryQuantity={item.InventoryQuantity}
      isOverSaleable={item.IsOverSaleable}
      discountDescription={item.DiscountDescription}
      originalPrice={item.OriginalPrice}
    />
  }
  const renderProductList = () => <OSwiper
    className={'product-detail-top-product-list-container'}
    data={useProductList}
    renderSwiperSlideContent={renderSwiperSlideContent}
    slidesPerView={commonState.isMobile ? 2 : 4}
  />

  const handleMinBtnClick = (item, qty) => {
    const newQty = qty - 1
    if (newQty < 1) {
      return dispatch(removeCartItem({item}))
    }
    dispatch(updateCartItemQuantity({item, quantity: newQty}))
  }
  const handleAddToCart = (item, qty) => {
    dispatch(addProductToCart({item, quantity: qty}))
  }
  const handleBeforeAddToCart = (item, qty) => {
    checkLogin(
      navigate,
      commonState.isLogged,
      location,
      () => handleAddToCart(item, qty)
    )
  }

  const handleChangeFavoriteStatus = (item) => {
    const isFavorite = item.IsFavorite
    const msg = isFavorite ? t('Cancel favorite successfully') : t('Add favorite successfully')
    const fun = isFavorite ? removeProductFromFavoriteList : addProductToFavoriteList
    fun(item).then(() => {
      message.success(msg)
      initPage()
    })
  }
  const handleBeforeChangeFavoriteStatus = (item, callback) => {
    checkLogin(
      navigate,
      isLogged,
      location,
      callback
    )
  }
  const handleFavoriteIconClick = (item, isProductDetail = false) => {
    handleBeforeChangeFavoriteStatus(item, () => handleChangeFavoriteStatus(item, isProductDetail))
  }
  
  const handleProductClick = item => {
    const path = `/productDetail/${item.ProductNum}`
    // if (location.pathname === path) {
    //   return
    // }
    navigate(path);
  }

  const renderProductGroupItem = (item, index) => <div
    className={'product-detail-more-product-item-container'}
    key={`product_group_2_item_${index}`}>
    <ProductItem
      img={item.Thumbnail} 
      price={item.Price} 
      name={item.Name}
      description={item.Description}
      isShowPrice={true}
      isFormatPriceToMoney={true}
      onBtnClick={() => handleBeforeAddToCart(item, 1)}
      onProductItemClick={() => handleProductClick(item)}
      isFavorited={item.IsFavorite}
      isShowFavorite={false}
      onFavoriteIconClick={() => handleFavoriteIconClick(item)}
      quantity={item.Quantity}
      btnType={getProductItemBtnType(item.Quantity)}
      onMinBtnClick={() => handleMinBtnClick(item, item.Quantity)}
      onPlusBtnClick={() => handleAddToCart(item)}
      isPromotion={item.IsPromotion}
      stock={item.InventoryQuantity}
      inventoryQuantity={item.InventoryQuantity}
      isOverSaleable={item.IsOverSaleable}
      discountDescription={item.DiscountDescription}
      originalPrice={item.OriginalPrice}
    />
  </div>

  const renderRichTextDescription = () => {
    if (!!productDetail?.rich_text_description) {
      return <div className={'product-detail-rich-text-description-container'}>
        <div className={'product-detail-rich-text-description-title'}>{t('Description')}</div>
        <div className="product-detail-rich-text-description-content" dangerouslySetInnerHTML={{__html: productDetail?.rich_text_description}}/>
      </div>
    }
  }
  const renderMoreProduct = () => <div className={'product-detail-more-product-container'}>
    <div className={'product-detail-more-product-title'}>{t('MORE PRODUCT')}</div>
    <div className={'product-detail-more-product-list'}>
      {useMoreProducts.map((item, index) => renderProductGroupItem(item, index))}
    </div>
  </div>

  const renderProductInfo = () => {
    return <div className={'product-detail-top-info-right-container'}>
      <div className={'product-detail-top-info-right-product-description-container'}>
        <div className={'product-detail-top-info-right-product-description-item'}>
        {/*<div className={'product-detail-top-info-right-product-description-title'}>*/}
          {productDetail?.Name}
          {/* {productDetail?.Description} */}
        </div>
      </div>
      <div className={'product-detail-top-info-right-price'}>
        <span className={'price'}>
          {`$${getFormatMoney(productDetail.Price)}`}
        </span>
      </div>
    {productDetail?.ShortColorCode && (
      <div className={'product-detail-top-info-right-product-description-container'}>
        <div className="product-detail-top-info-right-product-description-title">
          {productDetail.ShortColorCode}
        </div>
      </div>
    )}
    {productDetail?.FrontPOColor && (
      <div className="product-detail-top-info-right-product-style-container">
        <div className="product-detail-top-info-right-product-style-title">
          <div className="product-detail-top-info-right-product-style-item">
            {productDetail.FrontPOColor}
          </div>
        </div>
      </div>
    )}
    {hasPromotion && <div className={'product-detail-top-info-right-product-promotion-container'}>
      <div className={'product-detail-top-info-right-product-promotion-title'}>
        {t('Promotion')}
      </div>
      <div className={'product-detail-top-info-right-product-promotion-item'}>
        {productDetail?.DiscountDescription.map(description => <div>
          {description}
        </div>)}
      </div>
    </div>}
    <div className={'product-detail-top-info-right-product-style-container'}>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-title'}>
          {t('Modal Number')}
        </div>
      </div>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-right-product-style-item'}>
          {productDetail.RefNum}
        </div>
      </div>
    </div>
    {/* productDetail?.PackageSize && (
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Pkg Size')}
          </div>
        </div>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-item'}>
            {productDetail.PackageSize}
          </div>
        </div>
      </div>
    )*/}
    {productDetail?.Color &&
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Color')}
          </div>
        </div>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-item'}>
            {productDetail.Color}
          </div>
        </div>
      </div>
    }
    {/*{productDetail?.group_products && (
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          <div className={'product-detail-top-info-right-product-style-title'}>
            {t('Size Options')}
          </div>
        </div>
        <SkuListView data={productDetail} changeSku={(detail)=>{setProductDetail(detail)}}/>
      </div>
    )}*/}
    {/* pdf, shipping link */}
    {productPdf.length > 0 && (
      <div className={'product-detail-top-info-right-product-style-container'}>
        <div className={'product-detail-top-info-right-product-style-box'}>
          {productPdf.map((item) => (
            <div className={'product-detail-top-info-full-product-style-title'} onClick={() => window.open(item.document_url, '_blank', 'noopener,noreferrer')}>
              <FilePdfOutlined />  {item.source_name}
            </div>
        ))}
        </div>
      </div>
    )}
    <div className={'product-detail-top-info-right-product-style-container'}>
      <div className={'product-detail-top-info-right-product-style-box'}>
        <div className={'product-detail-top-info-full-product-style-title'} onClick={() => navigate('/returnsRefunds')}>
          <QuestionCircleOutlined />  Shipping Information
        </div>
      </div>
    </div>
    {/* add to cart */}
    <div className={'product-detail-top-info-right-bottom-container'}>
      <div className={'product-detail-top-info-right-bottom-left-container'}>
        <div className={'product-detail-top-info-right-bottom-left-label'}>{t('QTY')}</div>
        <InputNumber
          className={'product-detail-top-info-right-bottom-left-input'}
          min={1}
          defaultValue={quantity}
          onChange={setQuantity}
          disabled={isOutOfStock}
        />
        <div className={'product-detail-top-info-right-bottom-left-label'}>{productDetail?.PackageType}</div>
      </div>
      {
        isOutOfStock ? (
          <div className="product-detail-top-info-right-bottom-btn-disabled">
            {t('Out of stock')}
          </div>
        ) : (
          <div
            className="product-detail-top-info-right-bottom-btn"
            onClick={() => handleBeforeAddToCart(productDetail, quantity)}
          >
            {t('Add to cart')}
          </div>
        )
      }
    </div>
  </div>
  }

  const isOutOfStock = productDetail?.IsOverSaleable ? false : productDetail?.InventoryQuantity <= 0
  const hasPromotion = productDetail?.DiscountDescription && productDetail?.DiscountDescription?.length > 0;
  return (
    <Layout
      isDetailPage={false}
      detailPageTitle={productDetail?.Name}>
      <div className={rootClassName}>
        <div className={'product-detail-top-container'}>
          <div className={'product-detail-top-title'}>
            <span>{productDetail?.ProductCategory1_id ? productCategoryMap[productDetail.ProductCategory1_id]?.Name : ''}</span>
            {/*<div>{productDetail?.Name}</div>*/}
          </div>
          <div className={'product-detail-top-info-container'}>
            <div className={'product-detail-top-info-left-container'}>
              {renderProductDetailImg()}
              {renderProductImgSwiper()} 
            </div>
            {/* {widthData > 1300 ? renderProductInfo() : null} */}
            {renderProductInfo()}
          </div>
        </div>
        {renderRichTextDescription()}
        {/*{renderMoreProduct()}*/}
      </div>
    </Layout>
  )
}
